// @flow
import React, {useState, useCallback, useEffect} from 'react';
// import {useHistory} from 'react-router-dom';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/styles';
import {Hidden} from '@material-ui/core';

import {colors} from '../../../../assets/styles/colors';
import {usePage} from '../../../page/utils';
import CustomEditor from '../components/CustomEditor/CustomEditor';
import HeaderTitle from '../components/HeaderTitle/HeaderTitle';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {useIsFeatureInTier} from '../../../../utils/component';

import type {SaveLegalRequest} from '../api';
import type {Terms} from '../duck';
import {useTypedSelector} from '../../../../store';
type Props = {
  saveStoreTermsConfig: (saveLegalRequest: SaveLegalRequest) => void,
  fetchStoreTermsConfig: () => void,
  isFetchingTerms?: boolean,
  isSaving?: boolean,
  terms?: Terms,
  saveTermsSuccess?: boolean,
  saveTermsError?: string,
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#FFFFFF',
    '& .w-md-editor-toolbar': {
      left: '0!important',
    },
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  containerEditor: {
    position: 'relative',
    borderRadius: 8,
    border: '1px solid #E8E8ED',
    padding: theme.spacing(1.5),
    backgroundColor: colors.grey200,
    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  hiddenBlock: {
    borderRadius: '8px 8px 0 0',
    backgroundColor: colors.grey200,
    height: theme.spacing(6),
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
      height: 0,
    },
  },
  buttonSave: {
    marginRight: theme.spacing(2),
    color: colors.grey900,
    height: 24,
    fontSize: 14,
    fontWeight: 600,
    border: '1px solid #D4D4DD',
    padding: theme.spacing(1, 3),
    backgroundColor: '#FFFFFF',
  },
}));

function TermsAndConditions(props: Props) {
  const {
    terms,
    isSaving,
    saveTermsSuccess,
    saveTermsError,
    isFetchingTerms,
    saveStoreTermsConfig,
    fetchStoreTermsConfig,
  } = props;

  const legalSetting = useTypedSelector(
    state => state.legalConfig.legalSetting
  );

  const classes = useStyles();
  // const history = useHistory();
  const [description, setDescription] = useState('');

  const hasTermsNConditionsInformationFeature = useIsFeatureInTier(
    'STORE_POLICIES'
  );

  const handleSaveTerms = () => {
    if (description && description.length > 0) {
      saveStoreTermsConfig({legalConfigRequest: {enabled: true, description}});
    }
  };

  const handleDescription = useCallback((text: string) => {
    setDescription(text);
  }, []);

  useEffect(() => {
    if (terms && terms.description) {
      setDescription(terms.description);
    }
  }, [terms]);

  useEffect(() => {
    if (hasTermsNConditionsInformationFeature) {
      fetchStoreTermsConfig();
    }
  }, [fetchStoreTermsConfig, hasTermsNConditionsInformationFeature]);

  const shouldEnabled = hasTermsNConditionsInformationFeature;

  // useEffect(() => {
  //   if (saveTermsSuccess) history.push('/my-store/pages');
  // }, [history, saveTermsSuccess]);

  return (
    <div className={classes.root} data-color-mode="light">
      {(isFetchingTerms || isSaving) && <LoadingSpinner />}
      <SnackBar mode="error" message={!!saveTermsError ? saveTermsError : ''} />
      <SnackBar
        mode="success"
        message={!!saveTermsSuccess ? 'Guardado correctamente.' : ''}
      />
      <HeaderTitle
        title="Términos y condiciones"
        description="Describe las reglas, los términos y las pautas legales que todos deben aceptar para poder acceder y hacer uso de su página web."
        enabledKeyName="termsOfServiceInformationEnabled"
        enabled={legalSetting?.termsOfServiceInformationEnabled}
        disabled={!shouldEnabled}
      />
      <div className={classes.containerEditor}>
        <div className={classes.hiddenBlock}>
          {hasTermsNConditionsInformationFeature && (
            <Hidden smDown>
              <CustomButton
                disabled={!hasTermsNConditionsInformationFeature}
                className={classes.buttonSave}
                onClick={handleSaveTerms}
              >
                Guardar
              </CustomButton>
            </Hidden>
          )}
        </div>
        <CustomEditor
          selected
          noBorder
          height={252}
          idx={0}
          text={description}
          onChange={handleDescription}
          disabled={!hasTermsNConditionsInformationFeature}
        />
        {hasTermsNConditionsInformationFeature && (
          <Hidden mdUp>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                margin: '1rem 0 0 0',
              }}
            >
              <CustomButton
                disabled={!hasTermsNConditionsInformationFeature}
                className={classes.buttonSave}
                onClick={handleSaveTerms}
              >
                Guardar
              </CustomButton>
            </div>
          </Hidden>
        )}
      </div>
    </div>
  );
}

TermsAndConditions.propTypes = {
  saveStoreTermsConfig: PropTypes.func.isRequired,
  fetchStoreTermsConfig: PropTypes.func.isRequired,
  isFetchingTerms: PropTypes.bool,
  saveTermsSuccess: PropTypes.bool,
  saveTermsError: PropTypes.string,
  isSaving: PropTypes.bool,
  terms: PropTypes.object,
};

export default usePage<Props>(
  TermsAndConditions,
  'Terms and conditions configuration'
);
